import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Field, Formik, Form, ErrorMessage } from "formik";
import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import { setNewPassword, confirmNewPassword } from "../../api/authService";
import { Icons } from "../../Components/Icons/Icons";
import SignImage from "../../Assets/Images/sign-in.webp"

const NewPassSchema = Yup.object().shape({
  password: Yup.string()
    .min(14, "Password must be at least 14 characters")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[\^$*.\[\]{}()?\-"'!@#%&/,><':;|_~`]/,
      "Password must contain at least one special character"
    )
    .required("Please fill the field"),
});

interface NewPasswordData {
  password: string;
}

export const NewPassword = () => {
  const [error, setError] = useState("");
  const [firstPassword, setFirstPassword] = useState("");
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (values: NewPasswordData, setSubmitting: (isSubmitting: boolean) => void, resetForm: () => void) => {
    console.log("Submitting form with values:", values);
    try {
      const restorePasswordCode = sessionStorage.getItem("restorePasswordCode");
      const restorePasswordEmail = sessionStorage.getItem("restorePasswordEmail");

      if (restorePasswordCode && restorePasswordEmail) {
        await confirmNewPassword(values.password);
        setPasswordUpdated(true);
        sessionStorage.removeItem("restorePasswordCode");
        sessionStorage.removeItem("restorePasswordEmail");
        setError(``);
      } else {
        const session = await setNewPassword(values.password);
        if (session && typeof session.AccessToken !== "undefined") {
          sessionStorage.setItem("accessToken", session.AccessToken);
          if (sessionStorage.getItem("accessToken")) {
            setPasswordUpdated(true);
          } else {
            console.error("Session token was not set properly.");
          }
          console.log("Sign in successful", session);
        } else {
          console.error("SignIn session or AccessToken is undefined.");
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(`${error.toString().split(':')[1]}`);
      } else {
        setError("Unknown error");
      }
      setFirstPassword("");
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="sign">
      <img src={SignImage} alt="" />
      <div className="wrap">
        <div className="logo">
          {Icons.LogoIcon}
        </div>
        <div className="title">
          {passwordUpdated ? "Excellent, your password is updated" : firstPassword ? "Now, confirm your new password" : "Now, enter your new password"}
        </div>
        {error && (
          <Alert className="error" variant="outlined" severity="error">
            {error}
          </Alert>
        )}
        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          validationSchema={firstPassword ? Yup.object({
            confirmPassword: Yup.string()
              .oneOf([firstPassword], "Passwords do not match")
              .required("Please confirm your password")
          }) : NewPassSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            console.log("onSubmit called with values:", values);
            if (!firstPassword) {
              setFirstPassword(values.password);
              setSubmitting(false);
            } else if (values.confirmPassword === firstPassword) {
              handleSubmit(values, setSubmitting, resetForm);
            } else {
              setError("Passwords do not match");
              setFirstPassword("");
              resetForm();
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, touched, errors }) => (
            <Form>
              {!passwordUpdated && (
                !firstPassword ? (
                  <label htmlFor="password">
                    <Field
                      type="password"
                      name="password"
                      autoComplete="off"
                      className={touched.password && errors.password ? "error" : ""}
                    />
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="error-message"
                    />
                  </label>
                ) : (
                  <label htmlFor="confirmPassword">
                    <Field
                      type="password"
                      name="confirmPassword"
                      autoComplete="off"
                      className={touched.confirmPassword && errors.confirmPassword ? "error" : ""}
                    />
                    <ErrorMessage
                      name="confirmPassword"
                      component="div"
                      className="error-message"
                    />
                  </label>
                )
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn"
                onClick={() => passwordUpdated && navigate('/sign-in')}
              >
                {passwordUpdated ? "Log in" : firstPassword ? "Submit" : "Next"}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </section>
  );
};
